<template>
  <div id="containers-by-regulation" class="chemtracker-module">
    <div>
      <a id="csv-export-link" ref="#" @click="downloadListingCsv"
        ><b-img
          :src="raftHelper.getCsvIconPath()"
          width="24"
          height="24"
          fluid
          alt="CSV Download"
      /></a>
    </div>
    <br />
    <b-form>
      <fieldset class="chemtracker-filters chemical-containers-filters">
        <b-row>
          <b-col>
            <multi-select-filter
              :get-value="chemicalRegulationList"
              id="regulation-fil"
              label="Regulations"
              display-value="title"
              store-watch="getSelectedRegulations"
              :update-method="updateRegulations"
              :multiple="true"
              track-by="key"
            />
          </b-col>
          <b-col>
            <short-text-filter
              id="chem-name-fil"
              label="Chemical Name"
              class="chemtracker-container-text-filter"
              :get-value="chemNameFilter"
              :update-method="updateChemNameFilter"
            />
          </b-col>
          <b-col>
            <short-text-filter
              id="cas-num-fil"
              label="CAS Number"
              class="chemtracker-container-text-filter"
              :get-value="casFilter"
              :update-method="updateCasFilter"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <multi-select-filter
              :get-value="allBuildings"
              id="building-fil"
              label="Building"
              :current-set-value="firstSelectedBuilding"
              @update-filter="
                (payload) => updateBuilding(payload.itemsSelected)
              "
              :multiple="false"
              display-value="title"
              track-by="nid"
            />
          </b-col>
          <b-col>
            <multi-select-filter
              :get-value="allSpaces"
              id="space-fil"
              label="Location (Space)"
              store-watch="getSelectedSpacesFilter"
              :update-method="updateSpaces"
              display-value="title"
              :multiple="true"
              :disable="spaceDisabled"
              track-by="drupal_internal__nid"
            />
          </b-col>
          <b-col>
            <multi-select-filter
              :get-value="raftUnits.physicalStateOptions()"
              id="physical-fil"
              label="Physical State"
              :current-set-value="physicalStates"
              :update-method="updatePhysicalState"
              display-value="label"
              :multiple="true"
              track-by="key"
            />
          </b-col>
        </b-row>
      </fieldset>
    </b-form>
    <div v-if="containers.length > 0" id="view-pager-total">
      Displaying {{ raftHelper.displayingCurrent(currentPage, 25) }} -
      {{
        raftHelper.displayingCurrentTotal(
          totalItems,
          Math.ceil(containers.length / 25),
          currentPage,
          25,
        )
      }}
      of <strong>{{ totalItems }}</strong> results
    </div>
    <b-table
      id="reconciliation-history"
      striped
      hover
      responsive
      show-empty
      :no-sort-reset="true"
      :busy="isBusy"
      :fields="fields"
      :items="containers"
      ref="table"
      :class="{ 'table-pause-scroll': isBusy }"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <div v-if="$isB4" class="loading-throbber">
            <b-spinner class="align-middle" />
          </div>
          <div v-else>
            <img
              src="/sites/all/modules/hide_submit/images/squares.gif"
              alt="loading"
            />
          </div>
          <strong>Loading...</strong>
        </div>
      </template>
      <template #empty="scope">
        <div class="raftjs-empty-text">
          Please select a regulation to see results. If no chemicals are shown
          for a selected regulation, please adjust search parameters.
        </div>
      </template>
      <template #cell(chem_name)="row">
        <b-link :href="'/raft/chemdb/chemical/' + row.item.chem_uuid">
          {{ row.item.chem_name }}
        </b-link>
      </template>
      <template #cell(group_name)="row">
        <b-link :href="'/node/' + row.item.group_nid">
          {{ row.item.group_name }}
        </b-link>
      </template>
      <template #cell(space_title)="row">
        <b-link :href="'/node/' + row.item.space_nid">
          {{ row.item.space_title }}
        </b-link>
      </template>
      <template #cell(container_id)="row">
        <b-link :href="'/raft/chemtracker/container/' + row.item.uuid">
          {{ row.item.container_id }}
        </b-link>
      </template>
    </b-table>
    <b-pagination
      v-if="totalItems > itemsPerPage"
      v-model="currentPage"
      :total-rows="totalItems"
      :per-page="itemsPerPage"
      aria-controls="my-table"
      first-text="« first"
      prev-text="‹ previous"
      next-text="next ›"
      last-text="last »"
      align="center"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import MultiSelectFilter from "../filters/MultiSelectFilter.vue";
import ShortTextFilter from "../filters/ShortTextFilter.vue";
import raft_generic_helper from "../../lib/raft_generic_helper";
import raft_units from "../../lib/raft_units";
import { restClient } from "../../lib/raftApi";

export default {
  name: "ChemicalContainersRegulations",
  data() {
    return {
      raftHelper: raft_generic_helper,
      raftUnits: raft_units,
      unwatchHooks: [],
      itemsPerPage: 25,
      currentPage: 1,
      isBusy: false,
      totalItems: 0,
      totalPages: 0,
      isLoading: true,
      spaceDisabled: true,
      spaces: [],
      regulations: [],
      building: {},
      queries: {
        items_per_page: 25,
        offset: 0,
      },
      csvQueries: {},
      physicalStates: [],
      containers: [],
      selectedStatus: "",
      casFilter: "",
      chemNameFilter: "",
    };
  },
  methods: {
    ...mapActions([
      "fetchBuildingList",
      "updateSelectedBuildings",
      "fetchBuildingSpaceList",
      "clearListedSpaces",
    ]),
    /**
     * updateBuilding
     * @param {Object[]} building - an array of 0 or 1 buildings objects
     */
    updateBuilding(building) {
      this.spaceDisabled = !building;
      this.clearSpaceFilter();
      if (building) {
        this.queries.building_nid = building?.nid;
        this.csvQueries.building_nid = building?.nid;
        this.fetchBuildingSpaceList(building?.nid);
      } else {
        delete this.csvQueries.building_nid;
        delete this.queries.building_nid;
      }
      this.updateSelectedBuildings([building]);
      this.getRegulatedChemicals();
    },
    clearSpaceFilter() {
      this.clearListedSpaces();
      delete this.csvQueries.space;
      delete this.queries.space;
    },
    updateSpaces(newSpaces) {
      this.spaces = newSpaces;
      const spaces = [];
      delete this.csvQueries.space;
      this.$store.commit("setSelectedSpacesFilter", newSpaces);
      for (let i = 0; i < newSpaces.length; i++) {
        spaces.push(newSpaces[i].drupal_internal__nid);
        this.csvQueries["space[" + i + "]"] = newSpaces[i].drupal_internal__nid;
      }
      this.queries.space = spaces;
      this.getRegulatedChemicals();
    },
    updateRegulations(newRegulations) {
      this.regulations = newRegulations;
      const regulations = [];
      delete this.csvQueries.chem_phys_state;
      this.$store.commit("setSelectedRegulations", newRegulations);
      for (let i = 0; i < newRegulations.length; i++) {
        regulations.push(newRegulations[i].key);
        this.csvQueries["regulation[" + i + "]"] = newRegulations[i].key;
      }
      this.queries.regulation = regulations;
      this.getRegulatedChemicals();
    },
    async getRegulatedChemicals() {
      this.isBusy = true;

      this.containers = [];
      if (!this.getSelectedRegulations.length > 0) {
        this.containers = [];
        this.isBusy = false;
      }
      const path =
        this.$store.getters.config.baseUrl +
        "/api/chemicals/report/containers.json";
      const queries = this.queries;
      const restResponseData = await restClient
        .get({ path, queries })
        .catch((e) => this.$showNotification("error", e.message));
      if (restResponseData) {
        if (restResponseData.length < 1) {
          this.isBusy = false;
          return;
        }
        this.totalItems =
          parseInt(restResponseData?.[0].total_count, 10) + this.queries.offset;
        this.containers = restResponseData;
        this.isBusy = false;
      }
    },
    updateCasFilter(value) {
      this.casFilter = value;
      if (value.length > 1) {
        this.csvQueries.cas_number = value;
        this.queries.cas_number = value;
      } else {
        delete this.queries.cas_number;
        delete this.csvQueries.cas_number;
      }
      this.getRegulatedChemicals();
    },
    updateChemNameFilter(value) {
      this.chemNameFilter = value;
      if (value.length > 1) {
        this.queries.chemical_name = value;
        this.csvQueries.chemical_name = value;
      } else {
        delete this.queries.chemical_name;
        delete this.csvQueries.chemical_name;
      }
      this.getRegulatedChemicals();
    },
    updatePhysicalState(newStates) {
      const states = [];
      this.physicalStates = newStates;
      delete this.queries.chem_phys_state;
      delete this.csvQueries.chem_phys_state;
      if (newStates.length > 0) {
        for (let i = 0; i < newStates.length; i++) {
          states.push(newStates[i].key);
          this.csvQueries["chem_phys_state[" + i + "]"] = newStates[i].key;
        }
        this.queries.chem_phys_state = states;
      }
      this.getRegulatedChemicals();
    },
    downloadListingCsv() {
      const path = "/api/chemicals/report/containers.csv";
      const queries = this.csvQueries;
      const filename = "containers.csv";
      restClient
        .downloadFileStream({ path, queries, filename })
        .catch((e) => this.$showNotification("error", e.message));
    },
  },
  mounted() {
    this.fetchBuildingList();
  },
  components: {
    MultiSelectFilter,
    ShortTextFilter,
  },
  computed: {
    ...mapGetters([
      "allBuildings",
      "getSelectedRegulations",
      "getSelectedBuildingsFilter",
      "allSpaces",
    ]),
    firstSelectedBuilding() {
      return this.getSelectedBuildingsFilter?.[0];
    },
    chemicalRegulationList: function () {
      if (!this.$isB4) {
        if (
          Drupal.settings.hasOwnProperty("chemtracker") &&
          Drupal.settings.chemtracker.hasOwnProperty("chemical_regulations")
        ) {
          const obvTypes = Object.keys(
            Drupal.settings.chemtracker.chemical_regulations,
          ).map((key) => ({
            key: key,
            title: Drupal.settings.chemtracker.chemical_regulations[key],
          }));
          return obvTypes;
        }
      }
      return [];
    },
    fields: function () {
      return [
        { key: "chem_name", label: "Chemical Name", sortable: false },
        { key: "chem_cas_num", label: "CAS #", sortable: false },
        { key: "chem_phys_state", label: "State", sortable: false },
        { key: "chem_amount", label: "Amount", sortable: false },
        { key: "chem_unit", label: "Units", sortable: false },
        { key: "group_name", label: "Group", sortable: false },
        { key: "space_title", label: "Location", sortable: false },
        { key: "container_id", label: "Container ID", sortable: false },
      ];
    },
  },
  watch: {
    currentPage: function () {
      if (this.currentPage <= 1) {
        this.queries.offset = 0;
      }
      this.queries.offset = 25 * (this.currentPage - 1);
      this.getRegulatedChemicals();
    },
  },
};
</script>

<style scoped></style>
